/**
 * Payments — two-step payment intake.
 *
 * Step 1: pick a payment account (Stripe / Simplify / PayWay, per tenant).
 * Step 2: fill customer + card details and process.
 *
 * Stripe path is fully live via Stripe Elements — card details never
 * touch this backend. Simplify + PayWay accounts queue the request
 * into `payments` with status `awaiting_capture` so the operator can
 * complete them in the respective merchant portal (browser-side
 * tokenisation for those two gateways is phase-2).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  loadStripe,
} from "@stripe/stripe-js";
import {
  Elements, PaymentElement, useStripe, useElements,
} from "@stripe/react-stripe-js";
import {
  CreditCard, ArrowLeft, Loader2, ShieldCheck, AlertTriangle,
  Wallet, Building2, Landmark, CheckCircle2, RefreshCw, ArrowRight,
} from "lucide-react";

const TITLES = ["Mr", "Mrs", "Ms", "Miss", "Dr"];
const PAYMENT_FOR_OPTIONS = [
  "Invoice payment", "Deposit", "Retainer", "Service fee",
  "Setup fee", "Domain / hosting", "Other",
];

const GATEWAY_ICON = {
  stripe: CreditCard,
  simplify: Building2,
  payway: Landmark,
};

const GATEWAY_ACCENT = {
  stripe: "from-indigo-500 to-purple-600",
  simplify: "from-amber-500 to-red-600",
  payway: "from-emerald-500 to-cyan-600",
};

/* ── Step 1 ─ Account selector ────────────────────────────────── */
function AccountSelector({ onPick }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/payments/accounts");
        setAccounts(data.accounts || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load accounts");
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return <div className="p-12 text-center">
      <Loader2 className="h-6 w-6 animate-spin inline text-zinc-400" />
    </div>;
  }
  if (!accounts.length) {
    return (
      <Card className="rounded-sm border-amber-200 bg-amber-50">
        <CardContent className="p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-700 mt-0.5" />
            <div className="text-sm text-amber-800">
              <div className="font-semibold">No payment accounts configured.</div>
              <div className="mt-1">
                Head to <span className="font-mono">Finance &amp; Payroll → Merchant Settings</span>
                {" "}to add a Stripe, Commbank Simplify, or Westpac PayWay account.
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="payments-account-selector">
      <div>
        <div className="font-display font-bold text-xl">Choose a payment account</div>
        <p className="text-xs text-zinc-500 mt-1">
          Which merchant account should this transaction be captured against?
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {accounts.map((a) => {
          const Icon = GATEWAY_ICON[a.gateway] || CreditCard;
          const accent = GATEWAY_ACCENT[a.gateway] || "from-zinc-500 to-zinc-700";
          const disabled = !a.configured;
          return (
            <button key={a.account_key}
              type="button"
              disabled={disabled}
              onClick={() => onPick(a)}
              className={`text-left border rounded-sm p-4 transition-shadow ${
                disabled
                  ? "border-zinc-200 opacity-60 cursor-not-allowed"
                  : "border-zinc-200 hover:shadow-md cursor-pointer bg-white"}`}
              data-testid={`account-tile-${a.account_key}`}>
              <div className="flex items-start gap-3">
                <div className={`h-10 w-10 rounded-sm grid place-items-center text-white bg-gradient-to-br ${accent} shrink-0`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase font-semibold tracking-wide text-zinc-500">
                    {a.gateway_label}
                  </div>
                  <div className="font-semibold text-sm mt-0.5 truncate">{a.name}</div>
                  {a.xero_tenant_name && (
                    <div className="text-[11px] text-zinc-500 truncate">
                      Xero: {a.xero_tenant_name}
                    </div>
                  )}
                  {a.merchant_id && (
                    <div className="text-[11px] text-zinc-500 truncate">
                      Merchant ID: {a.merchant_id}
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <Badge variant="outline"
                    className={`rounded-sm text-[10px] ${
                      a.mode === "live"
                        ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                        : "border-amber-300 bg-amber-50 text-amber-800"}`}>
                    {a.mode === "live" ? "LIVE" : (a.mode?.toUpperCase() || "TEST")}
                  </Badge>
                  {!a.configured && (
                    <Badge variant="outline" className="rounded-sm text-[9px] border-zinc-200 bg-zinc-50 text-zinc-500">
                      Keys missing
                    </Badge>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── Step 2 ─ Stripe card capture (Elements) ──────────────────── */
function StripeCardBlock({ onIntentReady }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [err, setErr] = useState(null);

  const confirmAndReturnPI = async () => {
    if (!stripe || !elements) return null;
    setProcessing(true); setErr(null);
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
      });
      if (error) {
        setErr(error.message || "Payment failed");
        return null;
      }
      onIntentReady(paymentIntent);
      return paymentIntent;
    } catch (e) {
      setErr(e.message || "Stripe error");
      return null;
    } finally { setProcessing(false); }
  };

  // Expose confirmation to the parent form via ref
  useEffect(() => {
    onIntentReady._confirm = confirmAndReturnPI;
    return () => { delete onIntentReady._confirm; };
  }, [stripe, elements]);

  return (
    <div className="space-y-2" data-testid="stripe-card-block">
      <Label className="label-overline">Card details</Label>
      <div className="border border-zinc-200 rounded-sm p-3 bg-white">
        <PaymentElement options={{ layout: "tabs" }} />
      </div>
      {err && (
        <div className="text-xs text-red-600 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" />{err}
        </div>
      )}
      {processing && (
        <div className="text-xs text-zinc-500 flex items-center gap-1">
          <Loader2 className="h-3 w-3 animate-spin" />Confirming with Stripe…
        </div>
      )}
    </div>
  );
}

/* ── Step 2 ─ Payment form ────────────────────────────────────── */
function PaymentForm({ account, onBack, onCompleted }) {
  const [bootstrap, setBootstrap] = useState(null);
  const [form, setForm] = useState({
    title: "", first_name: "", last_name: "", mobile: "", email: "",
    payment_for: "Invoice payment", invoice_ref: "", notes: "",
    amount_aud: "",
  });
  const [busy, setBusy] = useState(false);
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const confirmRef = useMemo(() => ({}), []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(
          `/payments/accounts/${encodeURIComponent(account.account_key)}`);
        setBootstrap(data);
        if (data.gateway === "stripe" && data.publishable_key) {
          setStripePromise(loadStripe(data.publishable_key));
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to init account");
      }
    })();
  }, [account.account_key]);

  // For Stripe: create PaymentIntent as soon as amount is a valid number so
  // Elements can render.
  useEffect(() => {
    if (bootstrap?.gateway !== "stripe") return;
    const amt = Number(form.amount_aud);
    if (!(amt > 0)) return;
    let ignore = false;
    (async () => {
      try {
        const { data } = await api.post("/payments/stripe/intent", {
          account_key: account.account_key,
          amount_aud: amt,
          payment_for: form.payment_for,
          invoice_ref: form.invoice_ref,
        });
        if (!ignore) setClientSecret(data.client_secret);
      } catch (e) {
        if (!ignore) toast.error(e.response?.data?.detail || "Failed to create Stripe intent");
      }
    })();
    return () => { ignore = true; };
  }, [form.amount_aud, bootstrap?.gateway]);

  const validate = () => {
    if (!form.first_name.trim()) return "First name is required";
    if (!form.last_name.trim()) return "Last name is required";
    if (!form.email.trim()) return "Email is required";
    if (!form.mobile.trim()) return "Mobile is required";
    if (!form.payment_for.trim()) return "Payment for is required";
    if (!(Number(form.amount_aud) > 0)) return "Payment amount must be greater than $0";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }

    setBusy(true);
    try {
      const payload = {
        ...form,
        amount_aud: Number(form.amount_aud),
        account_key: account.account_key,
      };
      if (bootstrap?.gateway === "stripe") {
        // Confirm on the browser first, then send the PI id to our backend
        if (!confirmRef._confirm) {
          toast.error("Stripe not ready — try again in a moment.");
          setBusy(false); return;
        }
        const pi = await confirmRef._confirm();
        if (!pi) { setBusy(false); return; }
        payload.stripe_payment_method_id = pi.id;
      }
      const { data } = await api.post("/payments/charge", payload);
      onCompleted(data, bootstrap);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Charge failed");
    } finally { setBusy(false); }
  };

  const isStripe = bootstrap?.gateway === "stripe";
  const isQueued = bootstrap && !bootstrap.supports_live_capture;

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="payment-form">
      <div className="flex items-center gap-3 flex-wrap">
        <Button type="button" variant="outline" size="sm" className="rounded-sm"
          onClick={onBack} data-testid="payments-back-btn">
          <ArrowLeft className="h-4 w-4 mr-1.5" />Change account
        </Button>
        <div className="flex-1">
          <div className="label-overline">{account.gateway_label}</div>
          <div className="font-semibold text-lg">{account.name}</div>
        </div>
        <Badge variant="outline"
          className={`rounded-sm ${account.mode === "live"
            ? "border-emerald-300 bg-emerald-50 text-emerald-800"
            : "border-amber-300 bg-amber-50 text-amber-800"}`}>
          {account.mode === "live" ? "LIVE" : "TEST/SANDBOX"}
        </Badge>
      </div>

      {isQueued && bootstrap.capture_note && (
        <div className="border border-amber-200 bg-amber-50 rounded-sm p-3 text-xs text-amber-800 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <div>{bootstrap.capture_note}</div>
        </div>
      )}

      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-6 space-y-4">
          <div className="font-display font-bold text-lg">Customer</div>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <div className="md:col-span-1 space-y-1.5">
              <Label className="label-overline">Title</Label>
              <Select value={form.title || "__none__"}
                onValueChange={(v) => set("title", v === "__none__" ? "" : v)}>
                <SelectTrigger className="rounded-sm" data-testid="pay-title">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">—</SelectItem>
                  {TITLES.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 space-y-1.5">
              <Label className="label-overline">First name *</Label>
              <Input value={form.first_name}
                onChange={(e) => set("first_name", e.target.value)}
                className="rounded-sm" data-testid="pay-first-name" />
            </div>
            <div className="md:col-span-3 space-y-1.5">
              <Label className="label-overline">Last name *</Label>
              <Input value={form.last_name}
                onChange={(e) => set("last_name", e.target.value)}
                className="rounded-sm" data-testid="pay-last-name" />
            </div>
            <div className="md:col-span-3 space-y-1.5">
              <Label className="label-overline">Mobile *</Label>
              <Input value={form.mobile}
                onChange={(e) => set("mobile", e.target.value)}
                className="rounded-sm" placeholder="+61 4xx xxx xxx"
                data-testid="pay-mobile" />
            </div>
            <div className="md:col-span-3 space-y-1.5">
              <Label className="label-overline">Email *</Label>
              <Input type="email" value={form.email}
                onChange={(e) => set("email", e.target.value)}
                className="rounded-sm" data-testid="pay-email" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-6 space-y-4">
          <div className="font-display font-bold text-lg">Payment</div>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <div className="md:col-span-3 space-y-1.5">
              <Label className="label-overline">Payment for *</Label>
              <Select value={form.payment_for}
                onValueChange={(v) => set("payment_for", v)}>
                <SelectTrigger className="rounded-sm" data-testid="pay-payment-for">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_FOR_OPTIONS.map((o) => (
                    <SelectItem key={o} value={o}>{o}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-3 space-y-1.5">
              <Label className="label-overline">Invoice / reference number</Label>
              <Input value={form.invoice_ref}
                onChange={(e) => set("invoice_ref", e.target.value)}
                className="rounded-sm" data-testid="pay-invoice-ref" />
            </div>
            <div className="md:col-span-6 space-y-1.5">
              <Label className="label-overline">Notes</Label>
              <Textarea rows={3} value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
                className="rounded-sm resize-none"
                data-testid="pay-notes" />
            </div>
            <div className="md:col-span-3 space-y-1.5">
              <Label className="label-overline">Payment amount (AUD) *</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">$</span>
                <Input type="number" min="0" step="0.01"
                  value={form.amount_aud}
                  onChange={(e) => set("amount_aud", e.target.value)}
                  className="rounded-sm pl-6"
                  placeholder="0.00"
                  data-testid="pay-amount" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {isStripe && (
        <Card className="rounded-sm border-zinc-200">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="font-display font-bold text-lg">Card details</div>
              <div className="flex items-center gap-1.5 text-[11px] text-zinc-500">
                <ShieldCheck className="h-3.5 w-3.5" />
                Secured by Stripe — card details never touch our servers
              </div>
            </div>
            {!stripePromise ? (
              <div className="text-xs text-zinc-500 italic">Loading Stripe…</div>
            ) : !clientSecret ? (
              <div className="text-xs text-zinc-500 italic">
                Enter a payment amount above to activate the card field.
              </div>
            ) : (
              <Elements stripe={stripePromise} options={{ clientSecret }}>
                <StripeCardBlock onIntentReady={confirmRef} />
              </Elements>
            )}
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end">
        <Button type="submit" disabled={busy}
          className="rounded-sm bg-[#002FA7] hover:bg-[#001f70] text-white h-11 px-6"
          data-testid="pay-process-btn">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            : <CreditCard className="h-4 w-4 mr-2" />}
          {isQueued ? "Queue for capture" : "Process payment"}
        </Button>
      </div>
    </form>
  );
}

/* ── PayPal at-a-glance tile ─────────────────────────────────── */
function PayPalTile() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    setBusy(true); setErr(null);
    try {
      const { data } = await api.get("/paypal/summary");
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Load failed");
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); }, []);

  if (!data && !busy && !err) return null;

  const money = (n, cur) => `${cur} ${Number(n || 0).toLocaleString("en-AU", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;

  return (
    <Card className="rounded-sm border-[#0070ba]/30 bg-gradient-to-br from-[#f5fbff] to-white"
          data-testid="paypal-tile">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="label-overline text-[#0070ba]">PayPal at a glance</div>
            <div className="font-display font-bold text-xl mt-0.5">
              {data
                ? `${data.account_count} connected account${data.account_count === 1 ? "" : "s"} · ${data.period?.label || ""}`
                : busy ? "Loading PayPal roll-up…" : "PayPal roll-up"}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={busy}
                    className="rounded-sm h-8"
                    data-testid="paypal-tile-refresh">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <RefreshCw className="h-3.5 w-3.5" />}
              <span className="ml-1.5">Refresh</span>
            </Button>
            <Button variant="outline" size="sm"
                    onClick={() => navigate("/payments/paypal")}
                    className="rounded-sm h-8"
                    data-testid="paypal-tile-open">
              Manage <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </div>
        </div>

        {err && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-sm p-2">
            {err}
          </div>
        )}

        {data && data.account_count === 0 && (
          <div className="text-xs text-zinc-500 py-2">
            No PayPal accounts connected yet.{" "}
            <button className="underline text-[#0070ba]"
                    onClick={() => navigate("/payments/paypal")}>
              Connect one →
            </button>
          </div>
        )}

        {data && data.currencies?.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.currencies.map((c) => (
              <div key={c.currency}
                   className="rounded-sm border border-[#0070ba]/20 bg-white p-3"
                   data-testid={`paypal-tile-cur-${c.currency}`}>
                <div className="flex items-baseline justify-between">
                  <div className="font-mono font-bold text-lg">{c.currency}</div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider">
                    {c.tx_count} tx
                  </div>
                </div>
                <div className="text-xl font-display font-extrabold text-[#0070ba] mt-1">
                  {money(c.available, c.currency)}
                </div>
                <div className="text-[10px] text-zinc-500">
                  Available · total {money(c.total, c.currency)}
                </div>
                <div className="grid grid-cols-2 gap-1 mt-2 text-[11px]">
                  <div className="rounded-sm bg-emerald-50 px-2 py-1">
                    <div className="text-[9px] text-emerald-700 uppercase">In</div>
                    <div className="font-bold text-emerald-800">
                      {money(c.money_in, c.currency)}
                    </div>
                  </div>
                  <div className="rounded-sm bg-red-50 px-2 py-1">
                    <div className="text-[9px] text-red-700 uppercase">Out</div>
                    <div className="font-bold text-red-800">
                      {money(c.money_out, c.currency)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {data?.per_account?.length > 1 && (
          <div className="pt-1">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
              Per account
            </div>
            <div className="flex flex-wrap gap-2">
              {data.per_account.map((a) => (
                <div key={a.account_id}
                     className={`text-[11px] rounded-sm border px-2 py-1 ${
                       a.error
                         ? "border-red-200 bg-red-50 text-red-700"
                         : "border-zinc-200 bg-white text-zinc-700"}`}
                     title={a.error || ""}>
                  <span className="font-semibold">{a.name}</span>{" "}
                  <span className="text-zinc-400">·</span>{" "}
                  <span className="uppercase tracking-wider text-[9px]">
                    {a.mode}
                  </span>
                  {!a.error && (
                    <>
                      {" "}<span className="text-zinc-400">·</span>{" "}
                      <span className="text-zinc-500">{a.tx_count} tx</span>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ── Success view ─────────────────────────────────────────────── */
function SuccessView({ result, bootstrap, onNew }) {
  const queued = result.status === "awaiting_capture";
  return (
    <Card className={`rounded-sm ${queued ? "border-amber-200" : "border-emerald-200"}`}
      data-testid="payment-result">
      <CardContent className="p-8 text-center space-y-3">
        <div className={`h-14 w-14 rounded-full mx-auto grid place-items-center ${
          queued ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
          {queued ? <AlertTriangle className="h-7 w-7" />
                  : <CheckCircle2 className="h-7 w-7" />}
        </div>
        <div className="font-display font-bold text-2xl">
          {queued ? "Payment queued for capture" : "Payment succeeded"}
        </div>
        <div className="text-sm text-zinc-600 max-w-md mx-auto">
          {queued
            ? bootstrap?.capture_note
              || "The payment details have been captured. Complete the transaction manually in the merchant portal."
            : `Charged AUD ${Number(result.amount_aud).toFixed(2)} via ${result.gateway}.`}
        </div>
        <div className="text-[11px] text-zinc-400 font-mono">
          Ref: {result.payment_id}
        </div>
        <div className="pt-4">
          <Button onClick={onNew} variant="outline" className="rounded-sm"
            data-testid="pay-another-btn">
            Take another payment
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/* ── Main page ────────────────────────────────────────────────── */
export default function PaymentsPage() {
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null);
  const [resultBootstrap, setResultBootstrap] = useState(null);

  const reset = () => { setResult(null); setSelected(null); setResultBootstrap(null); };

  return (
    <div className="p-6 space-y-6 max-w-5xl" data-testid="payments-page">
      <div className="flex items-start gap-3">
        <div className="h-12 w-12 grid place-items-center rounded-sm bg-gradient-to-br from-indigo-500 to-purple-700 text-white">
          <Wallet className="h-6 w-6" />
        </div>
        <div>
          <div className="label-overline">Finance &amp; Payroll</div>
          <h1 className="font-display font-extrabold text-4xl tracking-tight">Payments</h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
            Take a card payment against any configured merchant account —
            Stripe (live), Commbank Simplify or Westpac PayWay
            (currently queued for manual capture).
          </p>
        </div>
      </div>

      {result ? (
        <SuccessView result={result} bootstrap={resultBootstrap} onNew={reset} />
      ) : !selected ? (
        <>
          <PayPalTile />
          <AccountSelector onPick={setSelected} />
        </>
      ) : (
        <PaymentForm account={selected}
          onBack={() => setSelected(null)}
          onCompleted={(r, boot) => { setResult(r); setResultBootstrap(boot); }} />
      )}
    </div>
  );
}
